import {
    Box,
    Grid,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ButtonContained from "../../Common/ButtonContained";
import CustomDialog from "../../Common/CustomDialog";
import TextLabel from "../../Common/CommonTextLabel";
import CommonFileUpload from "../../Common/CommonFileUpload";
import { showLoader } from "../../../Redux/Slices/loaderSlice";
import { useDispatch } from "react-redux";
import {
    create_posts,
    get_posts,
    update_post,
    uploadImage,
} from "../../../Redux/Actions/action";
import ErrorHandler from "../../../Utils/handler";
import { toast } from "react-toastify";
import { BucketURL } from "../../../ApiSetup/Api";
import Assets from "../../Common/Assets";

function HeroBanner() {
    // ---------- Hook -----------
    const dispatch = useDispatch();

    // --------------- State -------------
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({});
    const [bannerList, setBannerList] = useState({});
    const [error, setError] = useState({});
    const [isEdit, setIsEdit] = useState(false);

    const handleClear = () => {
        setOpen(false);
        setIsEdit(false);
        setData({});
        setError({});
    };

    const validation = () => {
        let isValid = true;
        let error = {};
        if (!data.bannerImage) {
            error.bannerImage = "Banner URL is required";
            isValid = false;
        }
        setError(error);
        return isValid;
    };

    // --------------- Api ---------------
    const handleImageUpload = async (e) => {
        const { files, name } = e?.target;

        const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
        const validTypes = [...validImageTypes];

        const fileData = files[0];
        if (!validTypes.includes(fileData.type)) {
            setError((error) => ({
                ...error,
                bannerImageSize:
                    "Invalid file type. Only JPEG, PNG, JPG, MP4, WebM, and OGG are allowed.",
            }));
            return;
        }

        if (fileData.size > 10485760) {
            setError((error) => ({
                ...error,
                bannerImageSize:
                    "File size exceeds the 10 MB limit. Please upload a smaller file.",
            }));
            return;
        }

        const formData = new FormData();
        formData.append("image", files[0]);

        dispatch(showLoader(true));
        try {
            const result = await dispatch(uploadImage(formData));
            const error = ErrorHandler(result);

            if (error) {
                const uploadedFile = result?.payload?.data?.[0]?.image;

                setData((prevState) => ({
                    ...prevState,
                    [name]: uploadedFile,
                    isImage: validImageTypes.includes(fileData.type),
                }));

                setError((error) => ({
                    ...error,
                    bannerImageSize: "",
                    bannerImage: "",
                }));
            }
        } catch (err) {
            console.error("File upload failed:", err);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const _createUpdateBanner = async () => {
        if (validation()) {
            dispatch(showLoader(true));
            try {
                let body = {
                    "type": "Hero-Banner",
                    heroBannerDetails: {
                        bannerImage: data?.bannerImage,
                        status: 'Active'
                    }
                };
                if (isEdit && data?._id) {
                    body._id = data?._id;
                }
                const fn = isEdit ? update_post : create_posts;
                const result = await dispatch(fn(body));
                const error = ErrorHandler(result);
                if (error) {
                    toast.success(result?.payload?.message);
                    _get_posts();
                    handleClear();
                }
            } catch (error) {
                toast.error(error?.message || "An unknown error occurred");
            } finally {
                dispatch(showLoader(false));
            }
        }
    };

    const _get_posts = async () => {
        dispatch(showLoader(true));
        let body = {
            type: "Hero-Banner",
            page: 1,
            limit: 10,
        };
        try {
            const result = await dispatch(get_posts(body));
            const error = ErrorHandler(result);
            if (error) {
                setBannerList(result?.payload?.data);
            }
        } catch (error) {
            toast.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        _get_posts();
    }, []);

    return (
        <>
            <Grid container spacing={1} xs={12}>
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "20px", fontWeight: 500 }}>
                            Banner
                        </Typography>
                        <ButtonContained
                            text={
                                bannerList?.data?.[0]?.heroBannerDetails?.bannerImage
                                    ? "Update Banner"
                                    : "Add Banner"
                            }
                            onClick={
                                bannerList?.data?.[0]?.heroBannerDetails?.bannerImage
                                    ? () => {
                                        setOpen(true);
                                        setIsEdit(true);
                                        setData({
                                            ...bannerList?.data?.[0],
                                            bannerImage:
                                                bannerList?.data?.[0]?.heroBannerDetails?.bannerImage,
                                        });
                                    }
                                    : () => {
                                        setOpen(true);
                                    }
                            }
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <img
                        src={
                            BucketURL + bannerList?.data?.[0]?.heroBannerDetails?.bannerImage
                        }
                        height={"100%"}
                        width={"100%"}
                    />
                </Grid>
                {open && (
                    <CustomDialog
                        isOpen={open}
                        handleClose={handleClear}
                        title={isEdit ? "Update Banner" : "Add Banner"}
                        padding={"10px"}
                        width={400}
                        action={
                            <>
                                <ButtonContained
                                    text={isEdit ? "Update Banner" : "Add Banner"}
                                    onClick={_createUpdateBanner}
                                    minWidth={"100%"}
                                    marginTop={"10px"}
                                />
                            </>
                        }
                    >
                        <Grid container spacing={2} xs={12} ml={-1}>
                            <Grid item xs={12}>
                                <CommonFileUpload
                                    text={"Banner Image"}
                                    padding={{ xs: "5px 14px", sm: "30px 14px", lg: "35px 7px" }}
                                    accept="image/jpeg, image/png, image/jpg"
                                    labelTitle="image/jpeg, image/png, image/jpg Up to 10MB"
                                    name={"bannerImage"}
                                    onChange={(e) => handleImageUpload(e)}
                                    valid
                                />
                                {data?.bannerImage && (
                                    <Assets
                                        src={BucketURL + data?.bannerImage}
                                        height={"50px"}
                                        width={"50px"}
                                        alt="Banner Image"
                                    />
                                )}
                                <TextLabel
                                    title={!data?.bannerImage ? error?.bannerImage : ""}
                                    color="error"
                                />
                                <TextLabel
                                    title={data?.bannerImageSize ? "" : error?.bannerImageSize}
                                    color="error"
                                />
                            </Grid>
                        </Grid>
                    </CustomDialog>
                )}
            </Grid>
        </>
    );
}

export default HeroBanner;
